/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * FeatureActivation — the framework's decision engine and single entry point
 * for `isFeatureEnabled(feature)`.
 *
 * Resolution order (see the spec's activation flow):
 *   1. Override provider (if any) returns true/false → return it immediately.
 *   2. Look up the registry definition.
 *   3. Resolve the *effective* stage: the stage-override provider's stage when
 *      it supplies one, otherwise the registry stage. The registry is never
 *      modified — this is a read-time substitution only.
 *   4. Resolve stage visibility of the effective stage for the current build
 *      environment; if the stage is not visible → false (no service lookup).
 *   5. CLIENT_ONLY → true.
 *   6. SERVICE_CONTROLLED → stageVisible && resolved service-controlled state
 *      (from the feature's declared source, with IndexedDB cache fallback).
 *
 * The activation override keeps the highest priority: it is consulted before
 * the registry is even read, so a stage override can never change the answer
 * for a feature that is forced ON or OFF. Conversely a stage override never
 * reaches past step 4 — service state is still required for a
 * SERVICE_CONTROLLED feature.
 *
 * Kept synchronous and free of React/Redux/localStorage/network. Both optional
 * providers are injected at bootstrap (dev builds only); a release build leaves
 * them `null`, so both branches are skipped entirely.
 */

import {
  FeatureType,
  featureRegistry,
  resolveBuildEnvironment,
  resolveEffectiveStage,
  resolveStageVisibility,
  ServiceControlledFeatureResolver
} from "../featureActivation";
import type {
  BuildEnvironment,
  Feature,
  FeatureStage
} from "../featureActivation";
import type {
  IFeatureActivation,
  IFeatureOverrideProvider,
  IFeatureStageOverrideProvider
} from "../interfaces";

export class FeatureActivation implements IFeatureActivation {
  private static mInstance: FeatureActivation | null = null;

  private mOverrideProvider: IFeatureOverrideProvider | null = null;

  private mStageOverrideProvider: IFeatureStageOverrideProvider | null = null;

  private readonly mServiceResolver: ServiceControlledFeatureResolver =
    new ServiceControlledFeatureResolver();

  private constructor() {}

  public static getInstance(): FeatureActivation {
    if (FeatureActivation.mInstance === null) {
      FeatureActivation.mInstance = new FeatureActivation();
    }
    return FeatureActivation.mInstance;
  }

  /** Test seam — drops the singleton and both injected override providers. */
  public static resetInstance(): void {
    FeatureActivation.mInstance = null;
  }

  /**
   * Injects (or clears) the override provider. Called once at bootstrap in
   * dev-tools builds; never called in release, so the provider stays `null`.
   */
  public setOverrideProvider(provider: IFeatureOverrideProvider | null): void {
    this.mOverrideProvider = provider;
  }

  /**
   * Injects (or clears) the lifecycle stage-override provider. Called once at
   * bootstrap in dev-tools builds; never called in release, so the provider
   * stays `null` and every feature resolves at its registry stage.
   */
  public setStageOverrideProvider(
    provider: IFeatureStageOverrideProvider | null
  ): void {
    this.mStageOverrideProvider = provider;
  }

  /**
   * The stage this feature is actually evaluated at — the overridden stage when
   * a stage override is active, otherwise the registry stage. Exposed so the
   * debug screen can show the same value the engine resolves instead of
   * re-deriving it. Returns `null` for an unregistered feature.
   */
  public getEffectiveStage(feature: Feature): FeatureStage | null {
    const definition = featureRegistry[feature];
    if (definition === undefined || definition === null) {
      return null;
    }
    return resolveEffectiveStage(
      definition.stage,
      this.getStageOverride(feature)
    );
  }

  private getStageOverride(feature: Feature): FeatureStage | null {
    if (this.mStageOverrideProvider === null) {
      return null;
    }
    return this.mStageOverrideProvider.getStage(feature);
  }

  public isFeatureEnabled(feature: Feature): boolean {
    if (this.mOverrideProvider !== null) {
      const override: boolean | null =
        this.mOverrideProvider.getOverride(feature);
      if (override !== null) {
        return override;
      }
    }

    const definition = featureRegistry[feature];
    if (definition === undefined || definition === null) {
      return false;
    }

    // The stage override (dev builds only) substitutes for the registry stage
    // here and nowhere else — everything below evaluates identically.
    const effectiveStage: FeatureStage = resolveEffectiveStage(
      definition.stage,
      this.getStageOverride(feature)
    );

    const environment: BuildEnvironment = resolveBuildEnvironment();
    const isStageVisible: boolean = resolveStageVisibility(
      effectiveStage,
      environment
    );
    if (!isStageVisible) {
      return false;
    }

    if (definition.type === FeatureType.CLIENT_ONLY) {
      return true;
    }

    return this.mServiceResolver.resolve(feature, definition.serviceFeature);
  }
}
